<template>
  <div class="map-location-picker">
    <div class="map-location-picker__input-group">
      <el-input
        v-model="searchKeyword"
        placeholder="搜索地址或地点"
        clearable
        @keyup.enter="handleSearch"
      >
        <template #append>
          <el-button
            :icon="Search"
            :loading="searching"
            :disabled="!searchKeyword.trim() || searching"
            @click="handleSearch"
          >
            搜索
          </el-button>
        </template>
      </el-input>
    </div>

    <div
      v-if="loading || mapError"
      class="map-location-picker__status"
    >
      <el-icon
        v-if="loading"
        class="is-loading"
      >
        <Loading />
      </el-icon>
      <span>{{ mapError || '地图加载中...' }}</span>
    </div>

    <div
      ref="mapContainer"
      class="map-location-picker__map"
    />

    <div class="map-location-picker__actions" style="margin-top: 12px; padding: 12px; background: var(--jf-color-background); border-radius: 8px; border: 1px solid var(--jf-color-border); display: block !important; visibility: visible !important;">
      <el-button
        type="primary"
        size="large"
        class="map-location-picker__confirm-btn"
        style="width: 100%; height: 48px; font-size: 16px; font-weight: bold; display: block !important; visibility: visible !important;"
        @click="confirmLocation"
      >
        确认保存位置
      </el-button>
    </div>

    <div
      v-if="selectedLocation"
      class="map-location-picker__result"
    >
      <div class="result-item">
        <span class="result-label">选择地址：</span>
        <span class="result-value">{{ selectedLocation.address }}</span>
      </div>
      <div class="result-item">
        <span class="result-label">经度：</span>
        <span class="result-value">{{ selectedLocation.longitude }}</span>
      </div>
      <div class="result-item">
        <span class="result-label">纬度：</span>
        <span class="result-value">{{ selectedLocation.latitude }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { Loading, Search } from '@element-plus/icons-vue'
import { loadAMapScript } from '@/utils/mapLoader'
import type {
  AMapInstance,
  AMapMarker,
  AMapGeocoder,
  AMapPlaceSearch,
  AMapConstructor,
} from '@/types/amap'

interface SelectedLocation {
  address: string
  longitude: number
  latitude: number
}

const props = withDefaults(defineProps<{
  address?: string
  longitude?: number | string
  latitude?: number | string
  amapKey?: string
  amapSecret?: string
}>(), {
  address: '',
  longitude: undefined,
  latitude: undefined,
  amapKey: '',
  amapSecret: '',
})

const emit = defineEmits<{
  'update:address': [value: string]
  'update:longitude': [value?: number]
  'update:latitude': [value?: number]
}>()

const mapContainer = ref<HTMLElement>()
const searchKeyword = ref('')
const selectedLocation = ref<SelectedLocation | null>(null)
const loading = ref(true)
const searching = ref(false)
const mapError = ref('')
let map: AMapInstance | null = null
let marker: AMapMarker | null = null
let geocoder: AMapGeocoder | null = null
let placeSearch: AMapPlaceSearch | null = null
let resizeObserver: ResizeObserver | null = null

onMounted(async () => {
  try {
    const key = props.amapKey || import.meta.env.VITE_AMAP_KEY
    const secret = props.amapSecret || import.meta.env.VITE_AMAP_SECRET || import.meta.env.VITE_AMAP_SECURITY_JS_CODE

    if (!key) {
      mapError.value = '未配置高德地图 Key，请联系管理员'
      loading.value = false
      return
    }

    await loadAMapScript(key, secret)
    initMap()
    await nextTick()
    map?.resize()
    if (mapContainer.value && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => map?.resize())
      resizeObserver.observe(mapContainer.value)
    }

    loading.value = false

    if (props.longitude !== undefined && props.latitude !== undefined) {
      const lng = typeof props.longitude === 'string' ? parseFloat(props.longitude) : props.longitude
      const lat = typeof props.latitude === 'string' ? parseFloat(props.latitude) : props.latitude
      const hasStoredLocation = Number.isFinite(lng) && Number.isFinite(lat) && (lng !== 0 || lat !== 0)
      if (hasStoredLocation) {
        setMapCenter(lng, lat)
        reverseGeocode(lng, lat, props.address || undefined)
      } else if (props.address) {
        searchAddress(props.address)
      }
    } else if (props.address) {
      searchAddress(props.address)
    }
  } catch (error) {
    mapError.value = error instanceof Error ? error.message : '地图加载失败，请检查高德地图配置'
    loading.value = false
    console.error('地图加载失败:', error)
  }
})

onUnmounted(() => {
  resizeObserver?.disconnect()
  if (map) {
    map.destroy()
  }
})

function initMap() {
  if (!mapContainer.value || !window.AMap) return

  const AMap = window.AMap as AMapConstructor

  const defaultCenter: [number, number] = [116.397428, 39.90923]
  map = new AMap.Map(mapContainer.value, {
    zoom: 13,
    center: defaultCenter,
    viewMode: '2D',
  })

  // 创建标记
  marker = new AMap.Marker({
    position: defaultCenter,
    draggable: true,
    cursor: 'move',
  })

  marker.setMap(map)

  // 地理编码服务
  geocoder = new AMap.Geocoder({
    city: '全国',
    extensions: 'all',
  })

  // 地点搜索服务
  placeSearch = new AMap.PlaceSearch({
    city: '全国',
    pageSize: 10,
    extensions: 'all',
    type: '',
  })

  // 监听标记拖动结束事件
  marker.on('dragend', (event: { lnglat: { lng: number; lat: number } }) => {
    const position = event.lnglat
    console.log('标记拖动结束 - 经度:', position.lng, '纬度:', position.lat)
    reverseGeocode(position.lng, position.lat)
  })

  // 监听地图点击事件
  map.on('click', (event: { lnglat: { lng: number; lat: number } }) => {
    const position = event.lnglat
    console.log('地图点击 - 经度:', position.lng, '纬度:', position.lat)
    if (marker) {
      marker.setPosition([position.lng, position.lat])
    }
    reverseGeocode(position.lng, position.lat)
  })
}

function setMapCenter(lng: number, lat: number) {
  if (!map) return
  map.setCenter([lng, lat])
  map.setZoom(15)
  if (marker) {
    marker.setPosition([lng, lat])
  }
}

function handleSearch() {
  const keyword = searchKeyword.value.trim()
  if (!keyword || searching.value) return

  searching.value = true
  mapError.value = ''

  searchAddress(keyword)
}

function searchAddress(address: string) {
  if (!geocoder) {
    searching.value = false
    mapError.value = '地址搜索服务尚未就绪，请稍后重试'
    return
  }

  geocoder.getLocation(address, (status: string, result: any) => {
    if (status === 'complete' && result.geocodes && result.geocodes.length > 0) {
      const firstGeocode = result.geocodes[0]
      if (!firstGeocode) return
      const location = firstGeocode.location
      setMapCenter(location.lng, location.lat)
      searching.value = false
      updateSelectedLocation(firstGeocode.formattedAddress || address, location.lng, location.lat)
      return
    }
    // 地理编码失败，尝试地点搜索
    searchPlace(address)
  })
}

function searchPlace(keyword: string) {
  if (!placeSearch) {
    searching.value = false
    mapError.value = '地址搜索服务尚未就绪，请稍后重试'
    return
  }

  placeSearch.search(keyword, (status: string, result: any) => {
    searching.value = false
    
    if (status === 'complete' && result.poiList && result.poiList.pois && result.poiList.pois.length > 0) {
      const poi = result.poiList.pois[0]
      if (poi && poi.location) {
        const { location, name, address } = poi
        setMapCenter(location.lng, location.lat)
        updateSelectedLocation(`${name}${address ? ` ${address}` : ''}`, location.lng, location.lat)
        return
      }
    }
    // 搜索失败，提供更详细的错误信息
    if (status === 'no_data') {
      mapError.value = '未找到相关地址，请尝试更详细的关键词（如城市+具体地址）'
    } else if (status === 'error') {
      mapError.value = '搜索服务异常，请稍后重试'
    } else {
      mapError.value = '没有找到相关地址，请补充详细地址后重试'
    }
  })
}

function reverseGeocode(lng: number, lat: number, fallbackAddress?: string) {
  console.log('开始逆地理编码 - 经度:', lng, '纬度:', lat)
  if (!geocoder) {
    console.log('地理编码服务不可用，直接使用坐标')
    // 如果地理编码服务不可用，直接使用当前坐标显示确认按钮
    updateSelectedLocation(fallbackAddress || `经度: ${lng}, 纬度: ${lat}`, lng, lat)
    return
  }

  geocoder.getAddress([lng, lat], (status: string, result: { regeocode?: { formattedAddress: string } }) => {
    console.log('逆地理编码结果 - 状态:', status, '结果:', result)
    if (status === 'complete' && result.regeocode) {
      const address = result.regeocode.formattedAddress
      updateSelectedLocation(address, lng, lat)
      return
    }
    // 逆地理编码失败，使用备用地址或坐标显示确认按钮
    console.log('逆地理编码失败，使用备用地址')
    updateSelectedLocation(fallbackAddress || `经度: ${lng}, 纬度: ${lat}`, lng, lat)
  })
}

function updateSelectedLocation(address: string, longitude: number, latitude: number) {
  selectedLocation.value = {
    address,
    longitude,
    latitude,
  }
}

function confirmLocation() {
  console.log('确认位置按钮被点击')
  if (!selectedLocation.value) {
    console.log('没有选择位置，无法确认')
    ElMessage.warning('请先在地图上选择位置')
    return
  }
  console.log('确认位置 - 地址:', selectedLocation.value.address, '经度:', selectedLocation.value.longitude, '纬度:', selectedLocation.value.latitude)
  emit('update:address', selectedLocation.value.address)
  emit('update:longitude', selectedLocation.value.longitude)
  emit('update:latitude', selectedLocation.value.latitude)
  ElMessage.success('位置已确认保存')
}

// 监听外部地址变化，更新地图位置
watch(
  () => props.address,
  (newAddress) => {
    if (newAddress && newAddress !== selectedLocation.value?.address) {
      searchKeyword.value = newAddress
      if (geocoder) {
        geocoder.getLocation(newAddress, (status: string, result: { geocodes?: Array<{ location: { lng: number; lat: number } }> }) => {
          if (status === 'complete' && result.geocodes && result.geocodes.length > 0) {
            const firstGeocode = result.geocodes[0]
            if (firstGeocode) {
              const location = firstGeocode.location
              setMapCenter(location.lng, location.lat)
              updateSelectedLocation(newAddress, location.lng, location.lat)
            }
          }
        })
      }
    }
  },
)
</script>

<style scoped lang="scss">
.map-location-picker {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.map-location-picker__input-group {
  width: 100%;
}

.map-location-picker__status {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  color: var(--jf-color-text-secondary);
  font-size: 13px;
  background: var(--jf-color-background);
  border: 1px solid var(--jf-color-border);
  border-radius: 6px;
}

.map-location-picker__map {
  width: 100%;
  height: 400px;
  border: 1px solid var(--jf-color-border);
  border-radius: 8px;
  overflow: hidden;
}

.map-location-picker__actions {
  margin-top: 12px;
  padding: 12px;
  background: var(--jf-color-background);
  border-radius: 8px;
  border: 1px solid var(--jf-color-border);
}

.map-location-picker__confirm-btn {
  width: 100%;
  height: 48px;
  font-size: 16px;
  font-weight: bold;
}

.map-location-picker__result {
  padding: 12px;
  background: var(--jf-color-background);
  border-radius: 8px;
  border: 1px solid var(--jf-color-border);
  min-height: 80px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.confirm-location-button {
  margin-top: 12px;
  width: 100%;
  height: 48px;
  font-size: 16px;
  font-weight: bold;
}

.result-item {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
}

.result-label {
  color: var(--jf-color-text-secondary);
  font-size: 14px;
  min-width: 60px;
}

.result-value {
  color: var(--jf-color-text-primary);
  font-size: 14px;
  font-weight: 500;
}
</style>
